import { BackgroundTask } from '@/pages/tools/RevaluationTool/types';
import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import {
  CancelResult,
  cancelAllBackgroundTasks,
  cancelBackgroundTask,
  ingestStreamedTasks,
  pollBackgroundTasks,
} from './backgroundTasksThunks';
import { isTaskActive } from './constants';

interface BackgroundTasksState {
  /** Latest snapshot from the listing endpoint (active + recent finished). */
  tasks: BackgroundTask[];
  loading: boolean;
  /** True after at least one poll completes — lets the UI distinguish "empty" from "not loaded". */
  polledOnce: boolean;
  /** Set when a task finishes; rings the bell until the panel is opened. */
  hasUnseen: boolean;
  /** task_ids that finished but haven't been acknowledged (opened) yet. */
  unseenCompletedIds: string[];
  /**
   * task_ids the user has requested to cancel. Cancellation is cooperative, so
   * the backend may still report them running/queued for a while — we treat
   * these as "cancelling" (not active) until a poll confirms terminal status.
   */
  cancellingIds: string[];
  error: string | null;
}

const initialState: BackgroundTasksState = {
  tasks: [],
  loading: false,
  polledOnce: false,
  hasUnseen: false,
  unseenCompletedIds: [],
  cancellingIds: [],
  error: null,
};

const backgroundTasksSlice = createSlice({
  name: 'backgroundTasks',
  initialState,
  reducers: {
    // Called when the user opens the notification panel — acknowledges the
    // completions so the bell stops ringing.
    markTasksSeen(state) {
      state.hasUnseen = false;
      state.unseenCompletedIds = [];
    },
  },
  extraReducers: (builder) => {
    // Applies a fresh task-list snapshot — used identically whether it arrived
    // via the SSE stream (ingestStreamedTasks) or a one-shot poll.
    const applySnapshot = (
      state: BackgroundTasksState,
      action: PayloadAction<{
        tasks: BackgroundTask[];
        newlyCompletedIds: string[];
      }>,
    ) => {
      state.loading = false;
      state.polledOnce = true;
      state.tasks = action.payload.tasks;
      if (action.payload.newlyCompletedIds.length > 0) {
        state.hasUnseen = true;
        state.unseenCompletedIds = [
          ...state.unseenCompletedIds,
          ...action.payload.newlyCompletedIds,
        ];
      }
      // Drop "cancelling" markers once the backend confirms the task is no
      // longer active (terminal, or gone from the listing).
      state.cancellingIds = state.cancellingIds.filter((id) => {
        const task = action.payload.tasks.find((t) => t.task_id === id);
        return task ? isTaskActive(task) : false;
      });
    };

    builder.addCase(pollBackgroundTasks.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(pollBackgroundTasks.fulfilled, applySnapshot);
    builder.addCase(pollBackgroundTasks.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message ?? 'Failed to load background tasks';
    });

    // Stream frames carry the same snapshot shape; reuse the same handler.
    builder.addCase(ingestStreamedTasks.fulfilled, applySnapshot);

    // Optimistically flag cancelled runs so they stop showing as running/queued
    // immediately, even though the actual stop happens at the next trade boundary.
    const markCancelling = (
      state: BackgroundTasksState,
      action: PayloadAction<CancelResult>,
    ) => {
      if (action.payload.cancelledIds.length === 0) return;
      state.cancellingIds = Array.from(
        new Set([...state.cancellingIds, ...action.payload.cancelledIds]),
      );
    };
    builder.addCase(cancelBackgroundTask.fulfilled, markCancelling);
    builder.addCase(cancelAllBackgroundTasks.fulfilled, markCancelling);
  },
});

export const { markTasksSeen } = backgroundTasksSlice.actions;

export default backgroundTasksSlice.reducer;
